"use client"

import { useState, useMemo, useEffect } from "react"
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  // 追加: CommandShortcut のインポート
  CommandShortcut,
} from "@/components/ui/command"
import { Button } from "@/components/ui/button"
import { useQuery } from "@tanstack/react-query"
import Image from "next/image"
import { useRouter } from "next/navigation"
// 追加: 虫眼鏡アイコン (lucide-react など)
import { Search } from "lucide-react"

export interface Book {
  id: string
  title: string
  description: string
  date: string | null
  tags: string[]
  slug: string
  amazonUrl: string
  rakutenUrl: string
}

// 画像取得 (SingleBook と同じ)
async function fetchEyecatch(id: string) {
  const response = await fetch(`/api/notion/eyecatch/${id}`)
  const data = await response.json()
  if (data.eyecatch) {
    return data.eyecatch
  }
  throw new Error("Eyecatch not found")
}

// タイトル + アイキャッチ画像を表示
function CommandBookItem({
  book,
  onSelect,
}: {
  book: Book
  onSelect: () => void
}) {
  const {
    data: pic,
    error,
    isLoading,
  } = useQuery(["eyecatch", book.id], () => fetchEyecatch(book.id))

  if (isLoading) {
    return (
      <CommandItem disabled>
        <span>Loading image...</span>
      </CommandItem>
    )
  }

  if (error) {
    console.error("画像が読み込めませんでした", error)
    return (
      <CommandItem disabled>
        <span>Error loading image</span>
      </CommandItem>
    )
  }

  return (
    <CommandItem onSelect={onSelect}>
      {/* 左側にアイキャッチ画像 */}
      <div className="relative w-16 h-16 mr-2 flex-shrink-0">
        <Image
          src={pic}
          alt={book.title}
          fill
          className="object-cover rounded-sm border"
        />
      </div>
      <span>{book.title}</span>
    </CommandItem>
  )
}

export function CommandBooks({ books }: { books: Book[] }) {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")

  // Ctrl+K or Cmd+K ショートカット
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault()
        setOpen(true)
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [])

  // タイトルのみ検索
  const filteredBooks = useMemo(() => {
    const term = search.toLowerCase().trim()
    if (!term) {
      // 空文字なら全件表示
      return books
    }
    return books.filter((book) => book.title.toLowerCase().includes(term))
  }, [books, search])

  // タグごとにグルーピング
  const groupedByTag = useMemo(() => {
    const result: Record<string, Book[]> = {}
    filteredBooks.forEach((book) => {
      book.tags.forEach((tag) => {
        if (!result[tag]) {
          result[tag] = []
        }
        result[tag].push(book)
      })
    })
    return result
  }, [filteredBooks])

  return (
    <>
      {/* ボタンの左側に虫眼鏡アイコンを配置し、CommandShortcut で (Ctrl+K) を表示 */}
      <Button variant="outline" className="w-72" onClick={() => setOpen(true)}>
        <Search className="mr-2 h-4 w-4" />
        本のタイトルで検索
        {/* CommandShortcut を利用してショートカット表記 */}
        <CommandShortcut>Ctrl+K</CommandShortcut>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="タイトルを検索"
          onValueChange={(value) => setSearch(value)}
        />

        <CommandList>
          <CommandEmpty>該当する書籍が見つかりません</CommandEmpty>

          {Object.entries(groupedByTag).map(([tag, items]) => (
            <CommandGroup key={tag} heading={tag}>
              {items.map((book) => (
                <CommandBookItem
                  key={book.id}
                  book={book}
                  onSelect={() => {
                    router.push(`/book/${book.slug}`)
                  }}
                />
              ))}
            </CommandGroup>
          ))}

          <CommandSeparator />
        </CommandList>
      </CommandDialog>
    </>
  )
}
